import apiConfig                                        from 'res-api-cli/build/config';
import { getLocalStorageSettingValue, DEFAULT_API_URL } from './settings';
import { History }                                      from 'history';
import reportError                                      from "lib/errorReporting";
import recoverExpiredSession                            from "lib/expiredSessionRecovery";

/** The `override-api-url` localStorage escape hatch, used by the E2E harness. */
const apiUrlOverride = (): string | null => {
  const override: string | boolean = getLocalStorageSettingValue('api-url');
  return typeof override === 'string' && override ? override : null;
};

export const getServerURL = (): string => {
  const override = apiUrlOverride();
  if (override) return override;

  switch (window._env_?.REACT_APP_AWS_ENV) {
    case 'academy':
      return 'https://api.forseti-academy.org';
    case 'lumin-sandbox':
      return 'https://api.lumin-sandbox.co.uk';
    case 'staging':
      return 'https://api.os-test.me';
    case 'sandbox':
      return 'https://sandbox-api.os-test.me';
    case 'production':
      return 'https://case-api.ombudsman-services.org';
    case 'dr':
      return 'https://cms-api.lumin-dr.co.uk';
    default:
      return DEFAULT_API_URL;
  }
}

/**
 * CD-8740 — is the `has_session` marker cookie readable from the SPA origin in
 * this environment?
 *
 * Derived from the **same** environment switch as `getServerURL` above, on
 * purpose: whether the marker can be read is entirely a question of whether the
 * SPA host and the API host share a registrable domain, and that pairing is
 * defined right here. A second switch would drift.
 *
 * **The polarity is load-bearing.** `false` means "probe unconditionally", which
 * costs one round trip. `true` means "an absent marker is taken as proof there
 * is no session", which — if wrong for the environment — is a silent logout for
 * every user in it. So only hosts verified to share a registrable domain with
 * their API return `true`; academy, an unknown environment, and an overridden
 * API host all return `false`.
 *
 * @see lib/sessionMarker.ts
 */
export const isSessionMarkerEnabled = (): boolean => {
  // The API host has been pointed somewhere else, so we cannot reason about
  // whether the marker's Domain covers this origin.
  if (apiUrlOverride()) return false;

  switch (window._env_?.REACT_APP_AWS_ENV) {
    case 'lumin-sandbox':   // api.lumin-sandbox.co.uk
    case 'staging':         // www.os-test.me / api.os-test.me
    case 'sandbox':         // sandbox-api.os-test.me
    case 'production':      // case.ombudsman-services.org / case-api.ombudsman-services.org
    case 'dr':              // cms.lumin-dr.co.uk / cms-api.lumin-dr.co.uk
      return true;
    case 'academy':
      // lumin-academy.ombudsman-services.org vs api.forseti-academy.org —
      // different eTLD+1, so the marker can never be read here.
      return false;
    default:
      // Local development (no REACT_APP_AWS_ENV): SPA on localhost:8081 and API
      // on localhost:2999 share the host `localhost`, so a host-only marker is
      // readable. Any *other* unrecognised value is an environment nobody has
      // verified — fail open and probe.
      return !window._env_?.REACT_APP_AWS_ENV;
  }
}

export const configureApi = ({ history, dispatch }: { history: History; dispatch: (action: unknown) => unknown }) => {
  apiConfig.apiUrl = getServerURL();

  const navigateFor = (e: any, context?: object): Promise<never> => {
    switch (e.name) {
      case 'UnauthorizedException':
        history.push('/unauthorized');
        break;
      case 'ForbiddenException':
        history.push('/forbidden');
        break;
      case 'NotFoundException':
        history.push('/forbidden');
        break;
      case 'ValidationException':
        break;
      case 'BunnyPublisherTimeoutException':
        break;
      case 'AuthTokenExpiredException':
        history.push('/signout');
        history.push('/signin');
        break;
      default:
        reportError(e, `${e?.name} - Unhandled API exception`, { context });
        history.push('/unavailable');
        break;
    }

    return Promise.reject(e);
  };

  const onError = (e: any, context?: object, replay?: () => Promise<unknown>): Promise<unknown> =>
    // CD-8740: a 401 for a signed-in standard user is an expired cookie session, not a
    // permissions problem. Renew and replay, or sign out to /signin; everything else (a 403, a
    // 5xx, anonymous and referral visitors, calls to /sessions) is handled exactly as before.
    recoverExpiredSession(e, context, replay, { dispatch, fallback: (error) => navigateFor(error, context) })
    ?? navigateFor(e, context);

  // @ts-expect-error apiConfig has not been set up well in old world code
  apiConfig.onError = onError;
}

export default configureApi;
